import React, { useState, useEffect } from 'react';
import { useStore } from '../../store/StoreContext';
import { useConfirm } from '../../hooks/useConfirm';
import { useToast } from '../../hooks/useToast';
import { ColorSwatches, Button, listPanelCls, listRowCls } from '../../components/ui';
// Pull defaults from the canonical constants file so this page always
// matches the rest of the app. The prior hardcoded copies drifted and
// omitted the Cancelled status, which caused the Estimates page pill to
// fall back to Draft whenever an estimate was set to Cancelled.
import {
    DEFAULT_CLIENT_STATUSES,
    DEFAULT_ESTIMATE_STATUSES,
    DEFAULT_OPERATIONS_STATUSES,
    DEFAULT_PRIORITY_OPTIONS,
    DEFAULT_FURNITURE_CLASS_OPTIONS,
} from '../../lib/constants';

// Status-manager redesign (Jaylen, 2026-05-14):
//   • REMOVED ability to add new statuses. The status keys ('new_lead',
//     'unassigned', etc.) are referenced throughout the app code for
//     gating, transitions, dispatch-board logic, and notifications. A
//     user-added status has no behavioral wiring, so allowing it from
//     Settings was a footgun. Editable surface is now color + label
//     only.
//   • Sections are collapsed by default with a compact horizontal color
//     preview, so the page doesn't sprawl. Click to expand and adjust.
//   • Added a Reset-to-defaults button per section that restores the
//     canonical statuses (covers the cleanup case where a previous
//     "add status" experiment polluted app_config).

function StatusManager() {
    const { config, saveConfig } = useStore();
    const [clientStatuses, setClientStatuses] = useState(DEFAULT_CLIENT_STATUSES);
    const [estimateStatuses, setEstimateStatuses] = useState(DEFAULT_ESTIMATE_STATUSES);
    const [opsStatuses, setOpsStatuses] = useState(DEFAULT_OPERATIONS_STATUSES);
    const [priorityOptions, setPriorityOptions] = useState(DEFAULT_PRIORITY_OPTIONS);
    const [furnitureClassOptions, setFurnitureClassOptions] = useState(DEFAULT_FURNITURE_CLASS_OPTIONS);
    const [saving, setSaving] = useState(false);
    const { show: showToast } = useToast();

    useEffect(() => {
        try { setClientStatuses(JSON.parse(config.client_statuses || '[]') || DEFAULT_CLIENT_STATUSES); } catch { setClientStatuses(DEFAULT_CLIENT_STATUSES); }
    }, [config?.client_statuses]);

    useEffect(() => {
        try { setEstimateStatuses(JSON.parse(config.estimate_statuses || '[]') || DEFAULT_ESTIMATE_STATUSES); } catch { setEstimateStatuses(DEFAULT_ESTIMATE_STATUSES); }
    }, [config?.estimate_statuses]);

    useEffect(() => {
        try { setOpsStatuses(JSON.parse(config.operations_statuses || '[]') || DEFAULT_OPERATIONS_STATUSES); } catch { setOpsStatuses(DEFAULT_OPERATIONS_STATUSES); }
    }, [config?.operations_statuses]);

    useEffect(() => {
        try { setPriorityOptions(JSON.parse(config.priority_options || '[]') || DEFAULT_PRIORITY_OPTIONS); } catch { setPriorityOptions(DEFAULT_PRIORITY_OPTIONS); }
    }, [config?.priority_options]);

    useEffect(() => {
        try { setFurnitureClassOptions(JSON.parse(config.furniture_class_options || '[]') || DEFAULT_FURNITURE_CLASS_OPTIONS); } catch { setFurnitureClassOptions(DEFAULT_FURNITURE_CLASS_OPTIONS); }
    }, [config?.furniture_class_options]);

    const persist = async (configKey, updated) => {
        setSaving(true);
        try {
            await saveConfig(configKey, JSON.stringify(updated));
            if (configKey === 'client_statuses') setClientStatuses(updated);
            else if (configKey === 'estimate_statuses') setEstimateStatuses(updated);
            else if (configKey === 'operations_statuses') setOpsStatuses(updated);
            else if (configKey === 'priority_options') setPriorityOptions(updated);
            else if (configKey === 'furniture_class_options') setFurnitureClassOptions(updated);
            showToast('Saved');
        } catch(e) { showToast('Error: ' + e.message, 'error'); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-3">
            <StatusSection
                title="Client Statuses"
                description="Person lifecycle: tracks whether a client is a new lead, actively working with you, or inactive."
                configKey="client_statuses"
                defaults={DEFAULT_CLIENT_STATUSES}
                statuses={clientStatuses}
                setStatuses={setClientStatuses}
                onPersist={persist}
                saving={saving}
            />
            <StatusSection
                title="Estimate Statuses"
                description="Sales lifecycle: tracks an estimate from draft through sending to client acceptance."
                configKey="estimate_statuses"
                defaults={DEFAULT_ESTIMATE_STATUSES}
                statuses={estimateStatuses}
                setStatuses={setEstimateStatuses}
                onPersist={persist}
                saving={saving}
            />
            <StatusSection
                title="Operations Statuses"
                description="Job lifecycle: tracks a move job from unassigned through dispatch to completion."
                configKey="operations_statuses"
                defaults={DEFAULT_OPERATIONS_STATUSES}
                statuses={opsStatuses}
                setStatuses={setOpsStatuses}
                onPersist={persist}
                saving={saving}
            />
            <StatusSection
                title="Priority Tiers"
                description="Per-estimate priority — manually set by the brothers when finalizing the estimate. Drives callback-queue ordering and the priority chip on Clients + Estimates rows."
                configKey="priority_options"
                defaults={DEFAULT_PRIORITY_OPTIONS}
                statuses={priorityOptions}
                setStatuses={setPriorityOptions}
                onPersist={persist}
                saving={saving}
            />
            <StatusSection
                title="Furniture Class Tiers"
                description="Per-client quality tier — Good/Better/Best/Luxury. Descriptive, drives crew + care allocation internally."
                configKey="furniture_class_options"
                defaults={DEFAULT_FURNITURE_CLASS_OPTIONS}
                statuses={furnitureClassOptions}
                setStatuses={setFurnitureClassOptions}
                onPersist={persist}
                saving={saving}
            />
        </div>
    );
}

function StatusSection({ title, description, configKey, defaults, statuses, setStatuses, onPersist, saving }) {
    // In-app confirm (QA M2 NEW-9 native-dialog sweep).
    const { confirm: confirmDialog, confirmElement } = useConfirm();
    const [expanded, setExpanded] = useState(false);
    const [editing, setEditing] = useState(null);
    // Detect drift from defaults so the "Reset" button can show as
    // prominent only when there's actually something to reset.
    const isCustomized = JSON.stringify(statuses.map(s => ({ k: s.key, c: s.color, l: s.label })))
        !== JSON.stringify(defaults.map(s => ({ k: s.key, c: s.color, l: s.label })));

    const updateField = async (key, field, value) => {
        await onPersist(configKey, statuses.map(s => s.key === key ? { ...s, [field]: value } : s));
        setEditing(null);
    };

    const resetToDefaults = async () => {
        if (!(await confirmDialog({ title: `Reset ${title} to defaults?`, body: `The original colors and labels come back and any statuses added to ${title} are removed. Records already using a removed status keep it. This can't be undone.`, confirmLabel: `Reset ${title}`, danger: true }))) return;
        await onPersist(configKey, defaults);
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {/* Collapsed header — always visible. Compact summary with a
                horizontal strip of color dots so the user can scan all
                three status types at a glance without expanding. */}
            <button
                type="button"
                onClick={() => setExpanded(e => !e)}
                className="w-full flex items-center gap-4 px-5 py-4 hover:bg-slate-50 text-left transition-colors"
                aria-expanded={expanded}>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h2 className="text-base font-bold text-slate-900">{title}</h2>
                        <span className="text-[11px] font-semibold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{statuses.length}</span>
                    </div>
                    {description && <p className="text-xs text-slate-500 mt-0.5 truncate">{description}</p>}
                </div>
                {/* Color preview strip — visible while collapsed */}
                <div className="flex items-center gap-1 flex-shrink-0">
                    {statuses.map(s => (
                        <div key={s.key}
                            title={s.label}
                            className="w-4 h-4 rounded-full border border-white shadow-sm"
                            style={{ backgroundColor: s.color }} />
                    ))}
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                    className={`text-slate-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}>
                    <path d="M6 9l6 6 6-6"/>
                </svg>
            </button>

            {expanded && (
                <div className="border-t border-slate-200 px-5 py-4 bg-slate-50/40">
                    {/* Ruled list — one panel, hairline rows (lists.md §2). Rows aren't clickable → cursor-default. */}
                    <div className={listPanelCls}>
                        {statuses.map((s) => (
                            <div key={s.key}
                                className={`${listRowCls} cursor-default flex items-center gap-3 px-3 py-2.5`}>
                                {/* Status preview pill — double-click to rename */}
                                <div className="flex-shrink-0 w-[180px] rounded-md px-3 py-1.5 text-sm font-semibold text-center"
                                    style={{ backgroundColor: s.color, color: '#fff' }}>
                                    {editing === s.key ? (
                                        <input className="bg-transparent border-0 text-center w-full focus:outline-none font-semibold text-white placeholder-white/60"
                                            autoFocus value={s.label}
                                            onChange={e => {
                                                const val = e.target.value;
                                                setStatuses(prev => prev.map(st => st.key === s.key ? { ...st, label: val } : st));
                                            }}
                                            onBlur={e => updateField(s.key, 'label', e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') updateField(s.key, 'label', e.target.value); }} />
                                    ) : (
                                        <span onDoubleClick={() => setEditing(s.key)} title="Double-click to rename">{s.label}</span>
                                    )}
                                </div>
                                {/* Color swatches + Custom… (form-controls.md). The
                                    status's current color shows as an extra
                                    "now" swatch when it's off the palette. */}
                                <ColorSwatches
                                    size="sm"
                                    value={s.color}
                                    onChange={hex => updateField(s.key, 'color', hex)}
                                    ariaLabel={`${s.label} color`}
                                    className="flex-1"
                                />
                            </div>
                        ))}
                    </div>

                    {/* Footer — reset only. No "add" surface; status keys are
                        wired into app logic and shouldn't grow at runtime. */}
                    <div className="flex items-center justify-end pt-2 border-t border-slate-200 mt-3">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={resetToDefaults}
                            disabled={saving || !isCustomized}>
                            Reset to defaults
                        </Button>
                    </div>
                </div>
            )}
            {confirmElement}
        </div>
    );
}

export default StatusManager;
